import {
  fadeHex,
  layerFill,
  normalizeHex,
  SVG_FILL_FADE,
  type Drawing2D,
  type DrawingLayer,
} from './lib'

// ---------------------------------------------------------------------------
// Cores escolhidas pelo utilizador, por CAMADA (categoria IFC).
//
// O desenho gerado (`Drawing2D`) já traz a cor da paleta do viewer em
// cada camada. O que o utilizador escolhe no popover "Camadas" fica
// SEPARADO, num mapa `nome da camada -> { stroke, fill }`, e só se
// aplica ao desenho na altura de o mostrar/exportar
// (`applyLayerStyles`). Duas consequências boas:
//   - o desenho original continua a ser a fonte dos VALORES POR OMISSÃO
//     (o botão "repor" é só apagar a entrada do mapa);
//   - o mapa é JSON puro, serializa directamente para o `SavedView2D` e
//     é retrocompatível (campo opcional; ausente = paleta).
//
// Tudo aqui é puro e testável fora do browser.
// ---------------------------------------------------------------------------

/** Cor do traço e do preenchimento de uma camada, quando o utilizador as
 *  escolheu. Campos ausentes = valor por omissão da paleta. */
export interface LayerStyle {
  stroke?: string
  fill?: string
}

export type LayerStyles = Record<string, LayerStyle>

/** Cor do traço de corte quando a camada não traz nenhuma (a mesma que
 *  os writers usam como recurso). */
export const DEFAULT_CUT_COLOR = '#111111'

/** Cor do traço de uma camada por omissão (paleta do viewer). */
export function defaultStroke(layer: DrawingLayer): string {
  return normalizeHex(layer.colorHex) ?? DEFAULT_CUT_COLOR
}

/** Cor do poché de uma camada por omissão: a cor do corte aproximada do
 *  branco (o mesmo que um alfa de 0,15 sobre papel, mas opaco). */
export function defaultFill(layer: DrawingLayer): string {
  return fadeHex(defaultStroke(layer), SVG_FILL_FADE)
}

/** Cor do poché que uma camada vai mesmo ter, já com a escolha do
 *  utilizador aplicada. */
export function effectiveFill(layer: DrawingLayer): string {
  return layerFill(layer, DEFAULT_CUT_COLOR)
}

/** true quando o mapa não tem nenhuma escolha efectiva. */
export function hasLayerStyles(styles: LayerStyles | undefined): boolean {
  if (!styles) return false
  for (const s of Object.values(styles)) {
    if (s && (s.stroke || s.fill)) return true
  }
  return false
}

/** Aplica as escolhas do utilizador a um desenho. Devolve o próprio
 *  desenho quando não há nada a mudar (não copiar à toa: o painel
 *  recalcula isto a cada render). */
export function applyLayerStyles(
  d: Drawing2D,
  styles: LayerStyles | undefined,
): Drawing2D {
  if (!hasLayerStyles(styles)) return d
  let touched = false
  const layers = d.layers.map((l) => {
    const s = styles?.[l.name]
    const stroke = normalizeHex(s?.stroke)
    const fill = normalizeHex(s?.fill)
    if (!stroke && !fill) return l
    touched = true
    return {
      ...l,
      colorHex: stroke ?? l.colorHex,
      fillHex: fill ?? l.fillHex,
    }
  })
  return touched ? { ...d, layers } : d
}

/** Escreve (ou limpa) uma cor de uma camada. Passar `undefined` repõe o
 *  valor por omissão; a entrada vazia é removida do mapa para o gravado
 *  não encher de objectos sem conteúdo. */
export function setLayerStyle(
  styles: LayerStyles,
  layerName: string,
  patch: LayerStyle,
): LayerStyles {
  const next: LayerStyles = { ...styles }
  const merged: LayerStyle = { ...next[layerName] }
  if ('stroke' in patch) {
    const v = normalizeHex(patch.stroke)
    if (v) merged.stroke = v
    else delete merged.stroke
  }
  if ('fill' in patch) {
    const v = normalizeHex(patch.fill)
    if (v) merged.fill = v
    else delete merged.fill
  }
  if (merged.stroke || merged.fill) next[layerName] = merged
  else delete next[layerName]
  return next
}

/** Apaga as escolhas de uma camada (botão "repor"). */
export function resetLayerStyle(
  styles: LayerStyles,
  layerName: string,
): LayerStyles {
  if (!styles[layerName]) return styles
  const next = { ...styles }
  delete next[layerName]
  return next
}

/** Valida o que vem do localStorage. Chaves não-string, cores inválidas
 *  ou lixo de outra versão são deitados fora sem levar o resto atrás. */
export function sanitizeLayerStyles(raw: unknown): LayerStyles | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const out: LayerStyles = {}
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!name || !value || typeof value !== 'object') continue
    const v = value as Record<string, unknown>
    const entry: LayerStyle = {}
    const stroke = normalizeHex(
      typeof v.stroke === 'string' ? v.stroke : undefined,
    )
    const fill = normalizeHex(typeof v.fill === 'string' ? v.fill : undefined)
    if (stroke) entry.stroke = stroke
    if (fill) entry.fill = fill
    if (entry.stroke || entry.fill) out[name] = entry
  }
  return Object.keys(out).length > 0 ? out : undefined
}
